// CSS Modules - component class name: FormInput__

<template>
  <div
    :class="[
      $style.container,
      { FormInput__reset: this.reset },
      { FormInput__file: this.type === 'file' },
      { FormInput__error: this.error },
      { FormInput__original: this.showOriginalValueState }
    ]"
  >
    <label
      :class="[$style.label, { FormInput__bold: this.boldLabel }]"
      :for="formInputId"
    >
      {{ label }}
      <span :class="$style.optional" v-if="optionalLabel">
        ({{ optionalLabel }}<span v-if="type === 'file'"
          >, max. {{ maxFiles }} x {{ maxFileSizeMB }} MB: {{ accept }}</span
        >)
      </span>
    </label>
    <!-- begin: special input file behaviour -->
    <div v-if="type === 'file'" :class="$style.selectedFiles">
      <span
        :class="$style.optional"
        v-if="noFilesLabel && (!selectedFiles || !selectedFiles.length)"
        >{{ noFilesLabel }}</span
      >
      <div
        v-else
        v-for="(selectedFile, index) in selectedFiles"
        :id="`file_${index}`"
        :key="index"
      >
        <Button
          class="FormInput__filesButton"
          text="X"
          colour="white"
          :isForm="false"
          isClose
          @close="deleteFile(index)"
        />
        <img :src="selectedFile.url" v-if="selectedFile.isImage" alt="" />
        {{ selectedFile.fullName }}, {{ selectedFile.size }} MB,
        {{ selectedFile.fileExtension }}
      </div>
      <span
        :class="$style.optional"
        v-if="maxFilesReachedLabel && maxFilesReached"
        >{{ maxFilesReachedLabel }} {{ selectedFiles.length }}</span
      >
    </div>
    <!-- end: special input file behaviour -->
    <input
      :id="formInputId"
      :type="type"
      :value="value"
      :maxlength="maxlength"
      :min="min"
      :placeholder="placeholder"
      @input="handleInput"
      @focus="handleInputFocus"
      @blur="handleInputBlur"
      @keyup="handleKeyUp"
      @change="handleChange"
      :aria-label="ariaLabel"
      :multiple="multiple"
    />
    <Button
      v-if="(type === 'file' && !multiple) || this.reset"
      text="X"
      colour="white"
      :isForm="false"
      isClose
      @close="resetValue"
    />
    <div v-if="error" :class="$style.errorMessage">{{ errorMessage }}</div>
    <div
      v-if="type === 'file' && invalidFiles.length > 0"
      :class="$style.errorMessage"
    >
      <div>{{ errorMessage }}</div>
      <div
        v-for="(invalidFile, index) in invalidFiles"
        :id="`invalidFile_${index}`"
        :key="index"
      >
        {{ invalidFile.fullName }} ({{ invalidFile.size }} MB |
        {{ invalidFile.fileExtension }})
      </div>
      <div>(max. {{ maxFiles }} | {{ maxFileSizeMB }} MB | {{ accept }})</div>
    </div>
  </div>
</template>

<script>
import Button from "../Button/Button";
import { v4 as uuidv4 } from "uuid";
import {
  ACCEPTED_DOCS,
  MAX_FILES,
  MAX_FILE_SIZE_MB,
  attachmentDefinition,
  attachedFile,
  isFileValid,
  isImageFile,
  isMaxFilesReached,
  getFileSizeMB,
  getFileExtension,
  getFileInitialData
} from "./helpers";

export default {
  name: "FormInput",
  components: {
    Button
  },
  props: {
    type: String,
    value: [String, Array, FileList],
    label: {
      type: String,
      default: ""
    },
    boldLabel: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: true
    },
    optionalLabel: String,
    errorMessage: {
      type: String,
      default: ""
    },
    error: {
      type: Boolean,
      default: false
    },
    reset: {
      type: Boolean,
      default: false
    },
    originalValue: [String, Array],
    formSubmitSwitch: {
      type: Boolean,
      default: false
    },
    placeholder: String,
    maxlength: String,
    min: String,
    ariaLabel: {
      type: String,
      default: undefined
    },
    noFilesLabel: String,
    maxFilesReachedLabel: String
  },
  data() {
    return {
      currentValue: "",
      showOriginalValueState: false,
      formInputId: uuidv4(),
      maxFilesReached: false,
      selectedFiles: [],
      invalidFiles: [],
      base64Urls: [],
      attachedFiles: [],
      attachment: {},
      file: {},
      accept: ACCEPTED_DOCS.join(", "),
      maxFileSizeMB: MAX_FILE_SIZE_MB,
      maxFiles: MAX_FILES
    };
  },
  mounted() {
    this.attachment = attachmentDefinition;
    this.file = attachedFile;
  },
  methods: {
    handleKeyUp($event) {
      this.$emit("keyup", $event);
    },
    handleInput($event) {
      if ($event.target.type !== "file") {
        this.currentValue = $event.target.value;
        this.$emit("input", $event.target.value);
      }
    },
    handleChange($event) {
      if ($event.target.type === "file" && $event.target.files && $event.target.files[0]) {
        this.invalidFiles = [];
        this.loadFiles($event.target.files);
      }
    },
    handleInputFocus() {
      if (
        this.originalValue &&
        (this.value === "" || this.value === this.originalValue)
      ) {
        this.showOriginalValueState = false;
        if (this.type !== "file") {
          this.$emit("input", "");
        }
      }
    },
    handleInputBlur() {
      if (this.originalValue && this.value === "") {
        this.showOriginalValueState = true;
        this.$emit("input", this.originalValue);
      }
    },
    resetValue() {
      this.$emit("input", this.originalValue);
    },
    deleteFile(index) {
      this.selectedFiles.splice(index, 1);
      this.base64Urls.splice(index, 1);
      this.attachedFiles.splice(index, 1);
      this.maxFilesReached = isMaxFilesReached(this.selectedFiles);
      this.invalidFiles = [];
      // update parent ContactForm FormData
      this.$emit("onFileListChanged", this.attachedFiles);
    },
    resetFile() {
      this.$nextTick(() => {
        this.file = getFileInitialData();
      });
    },
    loadFiles(fileList) {
      let fileCount = this.selectedFiles.length;
      for (const file of fileList) {
        const fileSize = getFileSizeMB(file),
          fileExtension = getFileExtension(file),
          fileFullName = file.name,
          fileName = file.name.split(".").shift(),
          isImage = isImageFile(file);
        if (fileCount < MAX_FILES && isFileValid(file)) {
          // Upload file if valid
          fileCount++;
          // Load base64url through FileReader API
          const reader = new FileReader();
          reader.onload = (event) => {
            // Upload file data
            this.file = {
              fullName: fileFullName,
              name: fileName,
              size: fileSize,
              type: file.type,
              fileExtension: fileExtension,
              isImage: isImage,
              url: event.target.result,
              isUploaded: true
            };
            this.selectedFiles.push(this.file);
            this.base64Urls.push(this.file.url);
            this.attachment = {
              filename: this.file.fullName,
              base64content: this.file.url.split(",").pop ()
            };
            this.attachedFiles.push(this.attachment);
            this.maxFilesReached = isMaxFilesReached(this.selectedFiles);
            // update parent ContactForm FormData
            this.$emit("onFileListChanged", this.attachedFiles);
          };
          reader.readAsDataURL(file);
          // reset this.file values
          this.resetFile();
        } else {
          // Add file info to error list
          this.file = {
            fullName: fileFullName,
            name: fileName,
            size: fileSize,
            type: file.type,
            fileExtension: fileExtension,
            isImage: isImage
          };
          this.invalidFiles.push(this.file);
        }
      }
    }
  },
  watch: {
    formSubmitSwitch: function(newValue, oldValue) {
      if (newValue !== oldValue && this.originalValue) {
        this.showOriginalValueState = true;
      }
    }
  }
};
</script>

<style module lang="scss">
@import "./FormInput.scss";
</style>
